/**
 * ================================================================================================
 *  WHERE THE HOME TILES SIT, AND IN WHAT ORDER — **two regions, each ordered on its own.**
 * ================================================================================================
 *
 * The Owner, 2026-10-29, replacing his own instruction of the day before:
 *
 *     *"נתוני השוק — מט״ח, ריבית, מדד ודלק — יהיו באזור הצד השמאלי, מתחת ל„שימוש לאחרונה”...
 *     מרכז דף הבית יוקדש לעבודה: תזרים, משימות ומועדפים... אזור צד ואזור מרכז יכולים להיות
 *     נפרדים מבחינת העיצוב, אבל שניהם צריכים לתמוך בסידור הכרטיסים המיועדים להם. אין לחזור
 *     למצב שבו כרטיסי הצד אינם ניתנים לסידור."*
 *
 * ── WHY THIS FILE HELD ONE LIST YESTERDAY AND HOLDS TWO TODAY ───────────────────────────────────
 *
 * It held FOUR ids and then FIFTEEN, in one list, because the drag complaint of 2026-10-28 was
 * *"אני לא יכול להזיז אותם איך שאני רוצה"* and the cause was that the home screen had two regions
 * with only one of them in the order model. Merging them into a single grid fixed the ordering and
 * **created a different defect**: fifteen tiles in two 508px columns is nine rows, a 1,525px page
 * in a 900px window, and 400px of the viewport unused — measured in a browser, not inferred.
 *
 * So the correction is not "one region" and never was. It is **two regions where BOTH are
 * ordered**. The side column is narrow by design — it carries reference readings a person glances
 * at — and the centre is where the work is. A tile belongs to exactly one of them, and an id in
 * neither list does not render at all, which is how a tile is retired from the home screen without
 * deleting the capability behind it.
 *
 * ── THE FOUR THAT LEFT, AND WHY THEY ARE NOT DELETED ────────────────────────────────────────────
 *
 *     *"הסר מתצוגת ברירת המחדל בבית את „משתמשי הארגון”, „המנוי”, „מקורות נתונים” ו„חברות
 *     הדורשות טיפול”. המידע יישאר במסכי הניהול המתאימים. אין למחוק את היכולות עצמן."*
 *
 * `members`, `subscription`, `sources` and `attention` are gone from both lists. Each one is still
 * reachable, and still answered by the same route, on the administration screen it belongs to —
 * what left is a card on the home screen, not a capability. **They were added the day before by
 * this workstream**, and the Owner's reason for removing them is worth keeping in writing:
 * *"אל תוסיף כרטיסי ניהול לדף הבית כדי להגדיל את מספר הווידג׳טים המחוברים."* Connecting an
 * administrative read and counting it as a connected widget is a measurement improving itself.
 */

/**
 * THE SIDE COLUMN — reference readings, under "recently used".
 *
 * Market data first, in the order the Owner named them, then the professional feed. None of these
 * is implemented yet; they render as absences, which is the point of having a place for them: a
 * screen that is ready and says it has no source is the Owner's instruction, and a screen that
 * invents a number is not.
 */
import type { PreferenceKey } from '../api/contract';
import type { TextKey } from '../i18n/catalogue';
import { readOnDevice, rememberPreference } from './preferences';

/*
 * `news` LEFT THIS LIST ON 2026-11-01, and nothing about it was deleted.
 *
 *     *"העדכונים המקצועיים יהיו בבאנר אופקי מתחלף בראש אזור התוכן."*
 *
 * The four that remain are NUMBERS, and a 162px column is the right shape for a number. A
 * headline is a sentence and needs width, so the feed took the one wide place on the screen —
 * `components/UpdatesBanner.tsx`, fed by the same `user.market.read` as these four.
 *
 * Retiring an id here is the whole retirement: `homeOrder` drops it from any order a device has
 * stored, `hiddenTiles` drops it from any hidden set, and the personalisation panel derives its
 * list from this array so it stops offering a card that no longer exists. Nothing had to be
 * remembered anywhere else, which is the property this file was given on 2026-10-29 and this is
 * the first time it has been used.
 */
export const DEFAULT_SIDE_ORDER = ['fx', 'rates', 'cpi', 'fuel'] as const;

/**
 * THE CENTRE — the work.
 *
 *     *"מרכז דף הבית יוקדש לעבודה: תזרים, משימות ומועדפים. ספריית המודולים תהיה נגישה בלי ליצור
 *     רצועה ריקה גדולה."*
 *
 * `library` is last and spans the row, because it is a list rather than a reading.
 *
 * ── THREE TILES LEFT THIS LIST ON 2026-11-01, AND THE ARGUMENT FOR ONE OF THEM WAS MINE ───────
 *
 * `companies`. This comment used to read: *"`companies` is here and not removed: it is the one
 * card of the five that answers 'what am I working on' rather than 'how is the office
 * configured', and the Owner's removal list does not name it."* The second half was true when it
 * was written and the Owner's section 6 names it now, in one sentence that answers the first
 * half too: *"אריח החברות אינו מוסיף — ניהול החברות נשאר במסך שלו."* A count of companies on the
 * home screen is a number with nowhere to go; the companies screen is where the work is.
 *
 * `today` and `urgent`. Section 4: *"הסר את כרטיסי המשימות הגדולים מהמרכז; הצג את המשימות
 * המתפרצות שלי כרצועה קומפקטית בתחתית אזור התוכן עם קישור למסך המשימות."* They are not deleted —
 * the reads, the screen and the capability are untouched — and what leaves is two large cards in
 * the position the Owner reserved for work he can act on. The strip that replaces them is not a
 * tile: it has no place in either order, it cannot be dragged and it cannot be switched off,
 * which is the same reasoning the updates banner carries.
 *
 * ── AND `library` RETIRED THE SAME EVENING, WITH THE HEADING ON `favourites`. ────────────────
 *
 *     *"להוריד מועדפים, וכל המודולים שלי גם להוריד — לא צריך את זה, אלא אם סימן בכוכב את
 *     המודול הרלוונטי ואז הוא יוצג בדף הבית."*
 *
 * `favourites` STAYS AS AN ID and loses its panel: it is the region a starred module renders
 * into, and with nothing starred it renders nothing at all. `library` is gone, and the list of
 * modules it held — the only place in the product where a star can be SET — is on
 * `TenantPersonalisationScreen`, which is the screen about which cards appear on a home screen.
 *
 * RETIRING AN ID HERE IS THE WHOLE RETIREMENT, which is the property this file was given on
 * 2026-10-29: `homeOrder` drops it from any order a device has stored, `hiddenTiles` drops it
 * from any hidden set, and the personalisation panel derives its list from this array so it stops
 * offering a card that no longer exists.
 */
export const DEFAULT_MAIN_ORDER = ['cashFlow', 'favourites'] as const;

/**
 * EVERY TILE THAT HAS A PLACE, AS A TYPE. **2026-11-01.**
 *
 * The two lists are `as const` so this union exists, and the union is what makes the
 * personalisation panel honest: its label table is `Record<HomeTileId, TextKey>`, so a tile added
 * to either region without a name a person can read **does not compile**. Before this, the panel
 * would simply have listed a card with no label, or omitted it — which is the quiet half of the
 * same defect the handle's `aria-label` had, where fifteen buttons announced the same words.
 */
export type HomeTileId =
  | (typeof DEFAULT_MAIN_ORDER)[number]
  | (typeof DEFAULT_SIDE_ORDER)[number];

/**
 * EVERY TILE'S NAME, BESIDE THE LIST OF TILES. **Moved here 2026-11-01.**
 *
 * It was in `TenantHomeScreen.tsx`, which was right while the home screen was the only reader.
 * The personalisation panel is a SETTINGS screen now, and the panel is what needs the names — so
 * a table in the home screen would have had to be exported from a screen to a screen, or copied.
 * Here it sits with the two arrays it is keyed on, which is the property worth keeping: a tile
 * added to either region without a name a person can read **does not compile**.
 *
 * The home screen still passes `labelHe` to `<Tile>`, because the grip composes its accessible
 * name from it — it now reads that name FROM HERE, so the name on the handle and the name in the
 * panel are one string and cannot drift.
 */
export const TILE_LABEL: Readonly<Record<HomeTileId, TextKey>> = {
  cashFlow: 'home.cashFlow',
  favourites: 'home.favourites',
  fx: 'home.widgets.fx',
  rates: 'home.widgets.rates',
  cpi: 'home.widgets.cpi',
  fuel: 'home.widgets.fuel',
};

/** Every tile that has a place, in either region. Nothing else renders. */
export const DEFAULT_HOME_ORDER: readonly string[] = [...DEFAULT_MAIN_ORDER, ...DEFAULT_SIDE_ORDER];

/** Which region a tile belongs to. A region is a property OF THE TILE, never of where it was dropped. */
export type HomeRegion = 'main' | 'side';

export function regionOf(id: string): HomeRegion | null {
  // The casts are the price of `as const` above: a tuple of literals has no `includes(string)`.
  if ((DEFAULT_MAIN_ORDER as readonly string[]).includes(id)) return 'main';
  if ((DEFAULT_SIDE_ORDER as readonly string[]).includes(id)) return 'side';
  return null;
}

/*
 * ── TWO PREFERENCES, AND THE OLD SINGLE-LIST KEY IS NOT READ ────────────────────────────
 *
 * `glpoint.homeOrder` held one list of fifteen ids under a model that no longer exists. Reading it
 * into either region would reconcile a stored list against a set it was never written for, and the
 * four removed ids would be silently dropped while the remaining eleven landed in an order that
 * meant something else. A new key per region is the honest migration: a device that ordered tiles
 * yesterday starts from the approved order today, once, and keeps everything it does from now on.
 *
 * ── AND SINCE §6.9, NEITHER STORAGE KEY IS THIS FILE'S BUSINESS ────────────────────────
 *
 * The Owner: *"ההגדרות יישמרו ויופיעו נכון לאחר רענון וכניסה מחדש."* `localStorage` answers the refresh
 * and cannot answer the second machine, so an order now lives on the ACCOUNT as well, and
 * `preferences.ts` is the one file that knows both homes. What changed here is small and it is the
 * right small: this file names a PREFERENCE, and where a preference is kept is somebody else's
 * problem. A `localStorage` call left in this file would have been a second writer, and a second
 * writer is how one of the two copies starts being the stale one.
 */
const PREFERENCE: Readonly<Record<HomeRegion, PreferenceKey>> = {
  main: 'home.order.main',
  side: 'home.order.side',
};

/**
 * ── AND THE THIRD PREFERENCE: WHICH CARDS THIS PERSON CHOSE TO SHOW. **2026-11-01.** ───────────
 *
 *     *"המשתמש בוחר אילו כרטיסים להציג ובאיזה סדר. הבחירה אינה מעניקה הרשאות או מנוי."*
 *
 * Two sentences and both are load-bearing. The first is a second choice beside the order, so it is
 * a second preference. The second is why this file is the right place for it and the server is
 * not: hiding a card is a statement about a SCREEN, not about an entitlement, and nothing here is
 * ever consulted when a route decides what a caller may read. A person who shows the collections
 * card sees the collections card saying it is not implemented; they do not thereby hold
 * `collections.*`, and no code path exists by which this list could grant it.
 *
 * IT HOLDS WHAT IS HIDDEN. `user-preference.ts` carries the full reasoning; the short form is that
 * a list of VISIBLE cards would make every tile a later release adds invisible to everybody who
 * had ever personalised their home screen, silently.
 */
const HIDDEN: PreferenceKey = 'home.hidden';

const DEFAULTS: Readonly<Record<HomeRegion, readonly string[]>> = {
  main: DEFAULT_MAIN_ORDER,
  side: DEFAULT_SIDE_ORDER,
};

/** What this machine has for one region. `[]` where it has nothing — the reconciliation below fills it. */
function stored(region: HomeRegion): readonly string[] {
  return readOnDevice(PREFERENCE[region]) ?? [];
}

/**
 * A MOVE IS SAVED TO BOTH HOMES, and the order of the two is the point: `rememberPreference` writes
 * this machine SYNCHRONOUSLY and pushes the account afterwards, best-effort. So a drop settles in
 * the frame it happened in, and a push that fails leaves exactly yesterday's behaviour — the move
 * holds here and does not reach the other machine.
 */
function save(region: HomeRegion, order: readonly string[]): void {
  rememberPreference(PREFERENCE[region], order);
}

/**
 * The stored order for one region, reconciled with what this release actually has.
 *
 * Kept, in order; then anything new, in the approved order. Never a tile that does not exist, and
 * never a tile left out — so a release that adds a tile does not leave an old device with a layout
 * that silently hides it, and a release that RETIRES one does not leave a dead id in the list.
 */
export function homeOrder(region: HomeRegion): readonly string[] {
  const approved = DEFAULTS[region];
  const saved = stored(region).filter((id) => approved.includes(id));
  const missing = approved.filter((id) => !saved.includes(id));
  return [...saved, ...missing];
}

/**
 * THE IDS THIS PERSON HAS HIDDEN, reconciled with what this release actually has.
 *
 * An id in neither region's list is dropped, exactly as it is from an order: a tile retired by a
 * release must not leave a name behind that a later release could accidentally reuse and find
 * already hidden.
 */
export function hiddenTiles(): readonly string[] {
  return (readOnDevice(HIDDEN) ?? []).filter((id) => regionOf(id) !== null);
}

/**
 * SHOW OR HIDE ONE CARD, and answer with the new hidden set.
 *
 * ABSENCE MEANS SHOWN, which is why this writes the exclusion rather than the inclusion, and why
 * a tile the caller does not know about is left exactly as it is.
 */
export function setTileShown(id: string, shown: boolean): readonly string[] {
  if (regionOf(id) === null) return hiddenTiles();
  const current = hiddenTiles();
  const next = shown
    ? current.filter((entry) => entry !== id)
    : current.includes(id)
      ? current
      : [...current, id];
  rememberPreference(HIDDEN, next);
  return next;
}

/**
 * BACK TO THE APPROVED HOME SCREEN. **2026-11-01.** *"ואיפוס לברירת המחדל."*
 *
 * THREE EMPTY LISTS AND NOT A DELETE, and that is the whole design of it. `homeOrder` reconciles a
 * stored order against the approved one, so an empty stored order IS the approved order -- and an
 * empty hidden set is every card shown. So a reset needs no route of its own, no `DELETE`, and no
 * second code path that could disagree with the read.
 *
 * It is also what makes the reset reach the OTHER MACHINE. A delete of the device copy would leave
 * the account holding yesterday's arrangement, which the next `hydratePreferences()` would hand
 * straight back -- the reset would appear to work and be undone on the next load. Writing an empty
 * value goes through `rememberPreference`, which writes this machine synchronously and pushes the
 * account after, exactly as a drag does.
 */
export function resetHome(): void {
  rememberPreference(PREFERENCE.main, []);
  rememberPreference(PREFERENCE.side, []);
  rememberPreference(HIDDEN, []);
}

/** Move one tile one place WITHIN ITS REGION, and answer with that region's new order. */
export function moveTile(region: HomeRegion, id: string, delta: -1 | 1): readonly string[] {
  const current = [...homeOrder(region)];
  const from = current.indexOf(id);
  const to = from + delta;
  if (from === -1 || to < 0 || to >= current.length) return current;

  const moved = current[from];
  const displaced = current[to];
  if (moved === undefined || displaced === undefined) return current;
  current[to] = moved;
  current[from] = displaced;

  save(region, current);
  return current;
}

/**
 * THE DROP: THE CARRIED TILE TAKES THE PLACE OF THE ONE IT WAS DROPPED ON.
 *
 * `moveTile` is one step and is what the keyboard does; this is what a pointer does, and the two
 * write through the same storage so a layout built by dragging and a layout built by arrows are
 * the same stored thing.
 *
 * ── IT REPLACED `placeTileBefore`, AND THE DIFFERENCE IS VISIBLE. **2026-11-01.** ─────────────
 *
 * "Before" is the right rule for a list with an insertion caret drawn between rows. This is a grid
 * of cards with no caret: the person drags a card ONTO another card, and what they expect is the
 * one they dropped to be where the other one was. Dragging the first card onto the third used to
 * move it one place -- the gesture did something, just not the thing it looked like.
 *
 * The index is the target's position BEFORE the removal, which is what produces both directions
 * correctly: downwards the target slides up, upwards it slides down. Taking `indexOf` again after
 * the removal is what "before" needs and what this must not do.
 *
 * **A DROP ACROSS REGIONS CHANGES NOTHING**, and that is deliberate rather than a limitation: a
 * market reading dropped into the work area would have to be re-laid-out for a 508px tile and a
 * work card dropped into the side column would have to survive 240px. The regions differ in width
 * by design, so membership is a property of the tile and the drag reorders within it. A drop on
 * the tile being dragged, or on a name that is not in that order, also changes nothing.
 */
export function placeTileAt(
  region: HomeRegion,
  draggedId: string,
  targetId: string,
): readonly string[] {
  const current = [...homeOrder(region)];
  const from = current.indexOf(draggedId);
  const to = current.indexOf(targetId);
  if (from === -1 || to === -1 || from === to) return current;

  current.splice(from, 1);
  current.splice(to, 0, draggedId);

  save(region, current);
  return current;
}
